import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type {
  ExternalServiceScript,
  ExternalServiceScriptPayload,
} from '../types/externalService';

/**
 * ID18 외부 서비스 스크립트 API. CMS 소유 서비스 = catalog 에 배치(be-catalog).
 * envelope { success, data } 를 adminFetch 로 호출한다. 관리자 목록은 plain array.
 * 경로(최종): `/admin/v1/ever-tv/external-services` — team-lead 가 게이트웨이에
 * ever-tv 라우트를 추가했고 be-catalog 컨트롤러가 이 경로로 확정됨. BO UI 라우트
 * (cms/ever-tv/external-services)는 화면경로라 API 경로와 무관 — 그대로 유지.
 */
const BASE = '/admin/v1/ever-tv/external-services';

interface ListEnvelope {
  success: boolean;
  data?: ExternalServiceScript[] | null;
}

interface SingleEnvelope {
  success: boolean;
  data?: ExternalServiceScript | null;
}

export const externalServicesQueryKey = () => ['admin', 'external-services'] as const;

const fetchList = async (): Promise<ExternalServiceScript[]> => {
  const res = await adminFetch(BASE);
  if (!res.ok) throw new Error(`external services list failed: ${res.status}`);
  const env = (await res.json()) as ListEnvelope;
  return env.data ?? [];
};

export const useExternalServicesQuery = () =>
  useQuery<ExternalServiceScript[]>({
    queryKey: externalServicesQueryKey(),
    queryFn: fetchList,
  });

export const useExternalServiceMutation = () => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: externalServicesQueryKey() });

  const create = useMutation({
    mutationFn: async (payload: ExternalServiceScriptPayload): Promise<ExternalServiceScript> => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', BASE, payload) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`external service create failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: ExternalServiceScriptPayload;
    }): Promise<ExternalServiceScript> => {
      const path = `${BASE}/${id}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', path, payload) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`external service update failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const path = `${BASE}/${id}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': idempotencyKeyFor('DELETE', path, '') },
      });
      if (!res.ok) throw new Error(`external service delete failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
};
